import React, { useState, useEffect } from 'react';
import { 
    Box, Paper,
    TextField, 
    FormControl, 
    InputLabel, 
    Select, Divider,
    MenuItem, 
    FormControlLabel, 
    Checkbox, 
    Radio, 
    RadioGroup, 
    Typography, 
    Button 
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ProgrammeSelector from '../../components/ProgrammeSelector';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';

const FormViewer = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [form, setForm] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const showToast = useCustomToast();
    // Load form data from sessionStorage when component mounts
    useEffect(() => {
        const storedForm = sessionStorage.getItem('viewForm');
        if (storedForm) {
            try {
                const parsedForm = JSON.parse(storedForm);
                setFormData(parsedForm);
                
                // Parse the formConfigJson
                const formConfig = typeof parsedForm.formConfigJson === 'string' 
                    ? JSON.parse(parsedForm.formConfigJson) 
                    : parsedForm.formConfigJson;
                setForm(formConfig);

                // Initialize form values
                if (formConfig?.sections) {
                    const initialValues = {};
                    formConfig.sections.forEach(section => {
                        section.fields.forEach(field => {
                            if (field.type === 'programme-selector') {
                                initialValues[field.id] = [];
                            } else {
                                initialValues[field.id] = field.type === 'checkbox' ? false : '';
                            }
                        });
                    });
                    setFormValues(initialValues);
                }
            } catch (error) {
                showToast('Error','Error parsing form data:', error,'error');
                // Handle error or redirect back
                navigate(-1);
            }
        } else {
            // No form data found, redirect back
            showToast('Info !','No form data found, redirected back','info')
            navigate(-1);
        }
    }, [navigate]);

    const handleInputChange = (fieldId, value) => {
        setFormValues(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleCancel = () => {
        showToast('Info !','Redirecting back','info')
        navigate(-1); // Go back to previous page
    };

    if (!formData || !form) {
        return <Box>Loading form...</Box>;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                    Category Type: {form.type} | Created: {new Date(formData.createdAt).toLocaleDateString()}
                    <br/>
                    Name: {form.name}
                </Typography>
                <Button 
                    variant="contained"
                    color='secondary'
                    startIcon={<FormatListBulletedIcon />}
                    onClick={handleCancel}
                >
                    List All Forms
                </Button>
            </Box>
            
            {form.sections.map((section) => (
                <Paper key={section.id} elevation={3} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h5" gutterBottom>{section.name}</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {section.fields.map((field) => {
                        switch (field.type) {
                            case 'label':
                                return (
                                    <Typography key={field.id} variant="h6" gutterBottom>
                                        {field.label}
                                    </Typography>
                                );
                            case 'email':
                            case 'number':
                                return (
                                    <TextField
                                        key={field.id}
                                        fullWidth
                                        label={field.label}
                                        margin="normal"
                                        required={field.required}
                                        multiline={field.type === 'textarea'}
                                        type={field.type === 'date' ? 'date' : field.type}
                                        value={formValues[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                    />
                                );
                            case 'select':
                                return (
                                    <FormControl key={field.id} fullWidth margin="normal" required={field.required}>
                                        <InputLabel>{field.label}</InputLabel>
                                        <Select 
                                            label={field.label}
                                            value={formValues[field.id] || ''}
                                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                                        >
                                            {field.options?.map((option, index) => (
                                                <MenuItem key={`${field.id}-option-${index}`} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                );
                            case 'checkbox':
                                return (
                                    <FormControlLabel
                                        key={field.id}
                                        control={
                                            <Checkbox
                                                checked={formValues[field.id] || false}
                                                onChange={(e) => handleInputChange(field.id, e.target.checked)}
                                                name={field.groupName || field.id}
                                            />
                                        }
                                        label={field.label}
                                    />
                                );
                            case 'radio':
                                return (
                                    <FormControl key={field.id} component="fieldset" margin="normal">
                                        <Typography variant="subtitle1">{field.label}</Typography>
                                        <RadioGroup
                                            value={formValues[field.id] || ''}
                                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                                            name={field.groupName || field.id}
                                        >
                                            {field.options?.map((option, index) => (
                                                <FormControlLabel
                                                    key={`${field.id}-radio-${index}`}
                                                    value={option}
                                                    control={<Radio />}
                                                    label={option}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                );
                            case 'file':
                                return (
                                    <Box key={field.id} margin="normal">
                                        <Typography variant="subtitle1">{field.label}</Typography>
                                        <Button variant="outlined" component="label">
                                            Upload File
                                            <input 
                                                type="file" 
                                                hidden 
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        handleInputChange(field.id, { 
                                                            fileName: file.name,
                                                            fileSize: file.size,
                                                            fileType: file.type,
                                                            file: file
                                                        });
                                                    }
                                                }}
                                            />
                                        </Button>
                                        {formValues[field.id]?.fileName && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Selected: {formValues[field.id].fileName} ({formatFileSize(formValues[field.id].fileSize)})
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            case 'textarea':
                                return (
                                    <TextField
                                        key={field.id}
                                        label={field.label}
                                        multiline
                                        rows={4}
                                        required={field.required}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        value={formValues[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                    />
                                );
                            case 'date':
                                return (
                                    <TextField
                                        key={field.id}
                                        label={field.label || "Date"}
                                        type="date"
                                        value={formValues[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                );
                            case 'programme-selector':
                                return (
                                    <Box key={field.id} margin="normal">
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            {field.label}
                                            {field.required && <span style={{ color: 'red' }}> *</span>}
                                        </Typography>
                                        <ProgrammeSelector
                                            field={field}
                                            value={formValues[field.id] || []}
                                            onChange={(selectedProgrammes) => handleInputChange(field.id, selectedProgrammes)}
                                            error={fieldErrors[field.id]}
                                        />
                                    </Box>
                                );
                            default:
                                return (
                                    <TextField
                                        key={field.id}
                                        label={field.label}
                                        required={field.required}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        value={formValues[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                    />
                                );
                        }
                    })}
                </Paper>
            ))}
        </Box>
    );
};

export default FormViewer;